import { Typography } from '@growdash/design-system/atoms';
import { useQuery } from '@tanstack/react-query';

// @ts-ignore

import AishaCard from '@growdash/growintel/components/atoms/aisha-card';
import { Brand } from '@growdash/growintel/generated';
import { useGrowintel } from '@growdash/growintel/hooks/useGrowIntel';
import { executeFacadeRequest } from '@growdash/growintel/lib/aisha';
import { Loader2 } from 'lucide-react';
import ReactMarkDown from 'react-markdown';

type Props = {
  isEmptyState?: boolean;
  watchlistBrands?: Brand[];
  brandNameForInsights?: string | null;
  className?: string;
};

export default function AishaWatchlist({
  isEmptyState,
  watchlistBrands,
  brandNameForInsights,
  className = '',
}: Props) {
  const { lib } = useGrowintel();
  const { aishaService } = lib;

  const competitorNamesString = watchlistBrands
    ?.map((wb) => wb.brand_name)
    .join(',');
  const { data, isFetching } = useQuery({
    queryKey: [
      'watchlist-aisha-insights',
      brandNameForInsights,
      competitorNamesString,
    ],
    queryFn: async (): Promise<any> => {
      if (!aishaService || !brandNameForInsights || !watchlistBrands?.length)
        return null;
      try {
        const payload = {
          brand_name: brandNameForInsights,
          competitors_name: competitorNamesString,
        };

        const response: any = await executeFacadeRequest(
          'int-watchlist',
          aishaService,
          payload,
        );
        return response.result;
      } catch (err) {
        console.error('Error during aisha insight generation:', err);
      }
    },
    enabled: !!competitorNamesString?.length && !!brandNameForInsights,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  if (isEmptyState) {
    return (
      <AishaCard className={className}>
        <Typography className="mt-4 font-semibold text-secondary">
          Pick a brand to see where your competitors stand
        </Typography>
        <Typography className="mt-2 text-tertiary text-sm">
          Select a brand to get leading insights.
        </Typography>
      </AishaCard>
    );
  }

  return (
    <AishaCard className={className}>
      {isFetching ? (
        <div className="flex justify-center py-4 animate-pulse">
          <Loader2 className="animate-spin" />
        </div>
      ) : (
        <Typography className="animate-fade-in text-tertiary font-medium text-sm !leading-[1.25rem]">
          <ReactMarkDown>{data}</ReactMarkDown>
        </Typography>
      )}
    </AishaCard>
  );
}
